/**
 * FERP Portal - Unified Primitive Form
 *
 * A comprehensive, single dynamic form that manages and visualizes all 25 workflow
 * primitives across the FERP system. This component serves as the central nervous
 * system for understanding and manipulating the entire FERP workflow engine.
 *
 * Features:
 * - 5×5 interactive grid organized by lifecycle phase
 * - Click-to-expand detail panel for CRUD operations
 * - Real-time stats and health indicators
 * - Life-critical visual indicators
 * - Global search across all primitives
 *
 * CONSTRAINTS:
 * - Mantine v8.x only (NO MUI)
 * - Repository pattern via generated GraphQL hooks
 * - Multi-tenant isolation via useTenantId()
 * - Backend Snowflake IDs only
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  ThemeIcon,
  Tooltip,
  RingProgress,
  Alert,
  Loader,
  Center,
  TextInput,
  ActionIcon,
  Box,
  Collapse,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCircuitDiode,
  IconSettings,
  IconPlayerPlay,
  IconChartBar,
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconClock,
  IconSearch,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
  IconGraph,
  // DESIGN phase icons
  IconSitemap,
  IconFileText,
  IconArrowsSort,
  IconKey,
  // CONFIGURE phase icons
  IconUsers,
  IconUser,
  IconContract,
  IconLock,
  // EXECUTE phase icons
  IconCheckbox,
  IconRubberStamp,
  IconFile,
  IconListDetails,
  // MONITOR phase icons
  IconBolt,
  IconBell,
  IconRoute,
  IconFlag,
  IconArrowsExchange,
  // OPTIMIZE phase icons
  IconClipboardCheck,
  IconCalendar,
  IconServer,
  IconPlugConnected,
  IconTrendingUp,
} from '@tabler/icons-react';

import { usePrimitiveData, type PrimitiveType, type LifecyclePhase, type AnyPrimitive } from './hooks/usePrimitiveData';
import { usePrimitiveMutations } from './hooks/usePrimitiveMutations';
import { PrimitiveDetailPanel } from './PrimitiveDetailPanel';
import { PrimitiveFormModal } from './PrimitiveFormModal';
import { PrimitiveRelationshipGraph } from './PrimitiveRelationshipGraph';
import { notifications } from '@mantine/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface PrimitiveDefinition {
  id: PrimitiveType;
  name: string;
  phase: LifecyclePhase;
  icon: typeof IconCircuitDiode;
  description: string;
  lifeCritical: boolean;
  color: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PHASE_CONFIG: Record<LifecyclePhase, { label: string; color: string; icon: typeof IconCircuitDiode; description: string }> = {
  DESIGN: { label: 'Design', color: 'blue', icon: IconCircuitDiode, description: 'Define reusable process structures' },
  CONFIGURE: { label: 'Configure', color: 'grape', icon: IconSettings, description: 'Set up system behavior and access' },
  EXECUTE: { label: 'Execute', color: 'cyan', icon: IconPlayerPlay, description: 'Runtime execution units' },
  MONITOR: { label: 'Monitor', color: 'orange', icon: IconChartBar, description: 'Observability and tracking' },
  OPTIMIZE: { label: 'Optimize', color: 'pink', icon: IconTrendingUp, description: 'Reporting, scheduling, and continuous improvement' },
};

const PRIMITIVES: PrimitiveDefinition[] = [
  // DESIGN Phase
  { id: 'workflow', name: 'Workflow', phase: 'DESIGN', icon: IconSitemap, description: 'Process templates', lifeCritical: true, color: 'blue' },
  { id: 'template', name: 'Template', phase: 'DESIGN', icon: IconFileText, description: 'Reusable templates', lifeCritical: false, color: 'blue' },
  { id: 'dependency', name: 'Dependency', phase: 'DESIGN', icon: IconArrowsSort, description: 'Prerequisites', lifeCritical: false, color: 'blue' },
  { id: 'policy', name: 'Policy', phase: 'DESIGN', icon: IconShield, description: 'Policy rules', lifeCritical: true, color: 'blue' },
  { id: 'role', name: 'Role', phase: 'DESIGN', icon: IconKey, description: 'Access roles', lifeCritical: false, color: 'blue' },

  // CONFIGURE Phase
  { id: 'team', name: 'Team', phase: 'CONFIGURE', icon: IconUsers, description: 'Team groups', lifeCritical: false, color: 'grape' },
  { id: 'actor', name: 'Actor', phase: 'CONFIGURE', icon: IconUser, description: 'Identities', lifeCritical: true, color: 'grape' },
  { id: 'validation', name: 'Validation', phase: 'CONFIGURE', icon: IconCheck, description: 'Validation rules', lifeCritical: false, color: 'grape' },
  { id: 'contract', name: 'Contract', phase: 'CONFIGURE', icon: IconContract, description: 'SLAs', lifeCritical: true, color: 'grape' },
  { id: 'constraint', name: 'Constraint', phase: 'CONFIGURE', icon: IconLock, description: 'Boundaries', lifeCritical: false, color: 'grape' },

  // EXECUTE Phase
  { id: 'instance', name: 'Instance', phase: 'EXECUTE', icon: IconPlayerPlay, description: 'Running instances', lifeCritical: false, color: 'cyan' },
  { id: 'task', name: 'Task', phase: 'EXECUTE', icon: IconCheckbox, description: 'Work items', lifeCritical: true, color: 'cyan' },
  { id: 'approval', name: 'Approval', phase: 'EXECUTE', icon: IconRubberStamp, description: 'Approval gates', lifeCritical: true, color: 'cyan' },
  { id: 'document', name: 'Document', phase: 'EXECUTE', icon: IconFile, description: 'Documents', lifeCritical: false, color: 'cyan' },
  { id: 'queue', name: 'Queue', phase: 'EXECUTE', icon: IconListDetails, description: 'Task routing', lifeCritical: false, color: 'cyan' },

  // MONITOR Phase
  { id: 'event', name: 'Event', phase: 'MONITOR', icon: IconBolt, description: 'Triggers', lifeCritical: false, color: 'orange' },
  { id: 'notification', name: 'Notification', phase: 'MONITOR', icon: IconBell, description: 'User alerts', lifeCritical: false, color: 'orange' },
  { id: 'trace', name: 'Trace', phase: 'MONITOR', icon: IconRoute, description: 'Execution traces', lifeCritical: false, color: 'orange' },
  { id: 'checkpoint', name: 'Checkpoint', phase: 'MONITOR', icon: IconFlag, description: 'Progress markers', lifeCritical: false, color: 'orange' },
  { id: 'transaction', name: 'Transaction', phase: 'MONITOR', icon: IconArrowsExchange, description: 'Financial records', lifeCritical: false, color: 'orange' },

  // OPTIMIZE Phase
  { id: 'audit', name: 'Audit', phase: 'OPTIMIZE', icon: IconClipboardCheck, description: 'Audit trail', lifeCritical: true, color: 'pink' },
  { id: 'analytics', name: 'Analytics', phase: 'OPTIMIZE', icon: IconChartBar, description: 'Data analysis', lifeCritical: false, color: 'pink' },
  { id: 'schedule', name: 'Schedule', phase: 'OPTIMIZE', icon: IconCalendar, description: 'Time triggers', lifeCritical: false, color: 'pink' },
  { id: 'resource', name: 'Resource', phase: 'OPTIMIZE', icon: IconServer, description: 'System resources', lifeCritical: false, color: 'pink' },
  { id: 'integration', name: 'Integration', phase: 'OPTIMIZE', icon: IconPlugConnected, description: 'External systems', lifeCritical: false, color: 'pink' },
];

const PHASES: LifecyclePhase[] = ['DESIGN', 'CONFIGURE', 'EXECUTE', 'MONITOR', 'OPTIMIZE'];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface PrimitiveCardProps {
  primitive: PrimitiveDefinition;
  total: number;
  active: number;
  lifeCritical: number;
  health: 'healthy' | 'degraded' | 'critical';
  loading?: boolean;
  onClick?: () => void;
}

function PrimitiveCard({ primitive, total, active, lifeCritical, health, loading, onClick }: PrimitiveCardProps) {
  const PrimitiveIcon = primitive.icon;

  const healthColor = {
    healthy: 'green',
    degraded: 'yellow',
    critical: 'red',
  }[health];

  const HealthIcon = {
    healthy: IconCheck,
    degraded: IconClock,
    critical: IconX,
  }[health];

  return (
    <Card
      withBorder
      padding="xs"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s, box-shadow 0.1s',
        ...(primitive.lifeCritical ? { borderLeft: '3px solid var(--mantine-color-red-6)' } : {}),
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Group justify="space-between" mb={4}>
        <Group gap={6}>
          <ThemeIcon size="xs" color={primitive.color} variant="light">
            <PrimitiveIcon size={12} />
          </ThemeIcon>
          <Text size="xs" fw={600}>{primitive.name}</Text>
        </Group>
        <Tooltip label={`Health: ${health}`}>
          <ThemeIcon size={14} color={healthColor} variant="light" radius="xl">
            <HealthIcon size={8} />
          </ThemeIcon>
        </Tooltip>
      </Group>

      <Text size="xs" c="dimmed" lineClamp={1} mb={4}>
        {primitive.description}
      </Text>

      {loading ? (
        <Loader size="xs" />
      ) : (
        <Group justify="space-between" ta="center">
          <Group gap={4}>
            <Text size="md" fw={700}>{total}</Text>
            <Text size="xs" c="dimmed">items</Text>
          </Group>

          {lifeCritical > 0 && (
            <Tooltip label={`${lifeCritical} life-critical`}>
              <Badge size="xs" color="red" variant="light">
                <Group gap={2}>
                  <IconAlertTriangle size={8} />
                  {lifeCritical}
                </Group>
              </Badge>
            </Tooltip>
          )}
        </Group>
      )}
    </Card>
  );
}

interface PhaseRowProps {
  phase: LifecyclePhase;
  primitives: PrimitiveDefinition[];
  primitiveData: ReturnType<typeof usePrimitiveData>;
  onPrimitiveClick: (primitive: PrimitiveDefinition) => void;
  expanded?: boolean;
  onToggle?: () => void;
}

function PhaseRow({ phase, primitives, primitiveData, onPrimitiveClick, expanded = true, onToggle }: PhaseRowProps) {
  const config = PHASE_CONFIG[phase];
  const PhaseIcon = config.icon;

  // Compute phase totals
  const phaseStats = useMemo(() => {
    let total = 0;
    let lifeCritical = 0;
    let hasCritical = false;
    let hasDegraded = false;

    primitives.forEach(p => {
      const data = primitiveData.getPrimitiveByType(p.id);
      total += data.stats.total;
      lifeCritical += data.stats.lifeCritical;
      if (data.stats.health === 'critical') hasCritical = true;
      if (data.stats.health === 'degraded') hasDegraded = true;
    });

    return {
      total,
      lifeCritical,
      health: hasCritical ? 'critical' : hasDegraded ? 'degraded' : 'healthy' as const,
    };
  }, [primitives, primitiveData]);

  return (
    <Paper withBorder mb="sm">
      {/* Phase Header */}
      <Group
        justify="space-between"
        p="xs"
        style={{
          cursor: onToggle ? 'pointer' : 'default',
          borderBottom: expanded ? '1px solid var(--mantine-color-gray-3)' : 'none',
        }}
        onClick={onToggle}
      >
        <Group gap="xs">
          {onToggle && (
            expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
          )}
          <ThemeIcon size="sm" color={config.color} variant="filled">
            <PhaseIcon size={14} />
          </ThemeIcon>
          <div>
            <Text size="sm" fw={600} tt="uppercase">{config.label}</Text>
            <Text size="xs" c="dimmed">{config.description}</Text>
          </div>
        </Group>
        <Group gap="xs">
          <Badge size="sm" color={config.color} variant="light">
            {phaseStats.total} items
          </Badge>
          {phaseStats.lifeCritical > 0 && (
            <Badge size="sm" color="red" variant="light">
              {phaseStats.lifeCritical} life-critical
            </Badge>
          )}
          <ThemeIcon
            size="sm"
            color={phaseStats.health === 'healthy' ? 'green' : phaseStats.health === 'degraded' ? 'yellow' : 'red'}
            variant="light"
            radius="xl"
          >
            {phaseStats.health === 'healthy' ? <IconCheck size={12} /> :
             phaseStats.health === 'degraded' ? <IconClock size={12} /> : <IconX size={12} />}
          </ThemeIcon>
        </Group>
      </Group>

      {/* Primitive Cards */}
      <Collapse in={expanded}>
        <SimpleGrid cols={5} spacing="xs" p="xs">
          {primitives.map(primitive => {
            const data = primitiveData.getPrimitiveByType(primitive.id);
            return (
              <PrimitiveCard
                key={primitive.id}
                primitive={primitive}
                total={data.stats.total}
                active={data.stats.active}
                lifeCritical={data.stats.lifeCritical}
                health={data.stats.health}
                loading={data.loading}
                onClick={() => onPrimitiveClick(primitive)}
              />
            );
          })}
        </SimpleGrid>
      </Collapse>
    </Paper>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function UnifiedPrimitiveForm() {
  const primitiveData = usePrimitiveData();
  const mutations = usePrimitiveMutations();
  const [selectedPrimitive, setSelectedPrimitive] = useState<PrimitiveDefinition | null>(null);
  const [panelOpened, { open: openPanel, close: closePanel }] = useDisclosure(false);
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] = useDisclosure(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingItem, setEditingItem] = useState<AnyPrimitive | undefined>(undefined);
  const [showRelationships, { toggle: toggleRelationships }] = useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPhases, setExpandedPhases] = useState<Record<LifecyclePhase, boolean>>({
    DESIGN: true,
    CONFIGURE: true,
    EXECUTE: true,
    MONITOR: true,
    OPTIMIZE: true,
  });

  // Handle primitive click
  const handlePrimitiveClick = useCallback((primitive: PrimitiveDefinition) => {
    setSelectedPrimitive(primitive);
    openPanel();
  }, [openPanel]);

  // Toggle phase expansion
  const togglePhase = useCallback((phase: LifecyclePhase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  }, []);

  // Handle create action
  const handleCreate = useCallback((type: PrimitiveType) => {
    setFormMode('create');
    setEditingItem(undefined);
    openFormModal();
  }, [openFormModal]);

  // Handle edit action
  const handleEdit = useCallback((item: AnyPrimitive) => {
    setFormMode('edit');
    setEditingItem(item);
    openFormModal();
  }, [openFormModal]);

  // Handle archive action (FERP is append-only - "delete" means archive)
  const handleArchive = useCallback(async (item: AnyPrimitive) => {
    if (!selectedPrimitive) return;

    // Extract item ID safely - not all fragment types have 'id' field
    const itemId = 'id' in item ? (item.id as string) :
                   'schemaId' in item ? (item.schemaId as string) :
                   'eventId' in item ? (item.eventId as string) : null;

    if (!itemId) {
      notifications.show({
        title: 'Error',
        message: 'Cannot archive: item has no ID',
        color: 'red',
      });
      return;
    }

    // Note: Archive mutations are primitive-specific, not all primitives have them
    // For now, show a placeholder notification
    notifications.show({
      title: 'Archive Not Available',
      message: `Archive functionality for ${selectedPrimitive.name} is under development.`,
      color: 'yellow',
    });
  }, [selectedPrimitive]);

  // Handle form success
  const handleFormSuccess = useCallback(() => {
    // Refetch all primitive data to show updates
    primitiveData.refetchAll();
  }, [primitiveData]);

  // Filter primitives by search
  const filteredPrimitives = useMemo(() => {
    if (!searchQuery) return PRIMITIVES;
    const q = searchQuery.toLowerCase();
    return PRIMITIVES.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.phase.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  // Group primitives by phase
  const primitivesByPhase = useMemo(() => {
    const grouped: Record<LifecyclePhase, PrimitiveDefinition[]> = {
      DESIGN: [],
      CONFIGURE: [],
      EXECUTE: [],
      MONITOR: [],
      OPTIMIZE: [],
    };
    filteredPrimitives.forEach(p => {
      grouped[p.phase].push(p);
    });
    return grouped;
  }, [filteredPrimitives]);

  // Get selected primitive data for panel
  const selectedPrimitiveData = selectedPrimitive
    ? primitiveData.getPrimitiveByType(selectedPrimitive.id)
    : null;

  return (
    <Container size="xl">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>25-Primitive System</Title>
            <Text c="dimmed">Unified management of all workflow primitives</Text>
          </div>
          <Group gap="md">
            {/* Global Stats */}
            <RingProgress
              size={60}
              thickness={6}
              sections={PHASES.map(phase => ({
                value: 20,
                color: PHASE_CONFIG[phase].color,
              }))}
              label={
                <Text ta="center" size="xs" fw={700}>
                  25
                </Text>
              }
            />

            {/* Life-Critical Alert */}
            {primitiveData.totalStats.lifeCritical > 0 && (
              <Alert
                color="red"
                variant="light"
                icon={<IconAlertTriangle size={16} />}
                p="xs"
              >
                <Text size="xs">{primitiveData.totalStats.lifeCritical} life-critical items</Text>
              </Alert>
            )}

            {/* System Health */}
            <Tooltip label={`System health: ${primitiveData.totalStats.health}`}>
              <ThemeIcon
                size="lg"
                color={primitiveData.totalStats.health === 'healthy' ? 'green' :
                       primitiveData.totalStats.health === 'degraded' ? 'yellow' : 'red'}
                variant="light"
                radius="xl"
              >
                {primitiveData.totalStats.health === 'healthy' ? <IconCheck size={20} /> :
                 primitiveData.totalStats.health === 'degraded' ? <IconClock size={20} /> : <IconX size={20} />}
              </ThemeIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Toolbar */}
        <Group>
          <TextInput
            placeholder="Search primitives..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1, maxWidth: 400 }}
          />
          <Tooltip label={showRelationships ? 'Hide relationships' : 'Show relationships'}>
            <ActionIcon
              variant={showRelationships ? 'filled' : 'light'}
              size="lg"
              onClick={toggleRelationships}
              color="grape"
            >
              <IconGraph size={18} />
            </ActionIcon>
          </Tooltip>
          <ActionIcon
            variant="light"
            size="lg"
            onClick={() => primitiveData.refetchAll()}
            loading={primitiveData.loading}
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>

        {/* Loading State */}
        {primitiveData.loading && (
          <Center py="xl">
            <Stack ta="center">
              <Loader size="lg" />
              <Text c="dimmed">Loading primitives...</Text>
            </Stack>
          </Center>
        )}

        {/* Phase Grid */}
        {!primitiveData.loading && (
          <Stack gap={0}>
            {PHASES.map(phase => {
              const phasePrimitives = primitivesByPhase[phase];
              if (phasePrimitives.length === 0) return null;
              return (
                <PhaseRow
                  key={phase}
                  phase={phase}
                  primitives={phasePrimitives}
                  primitiveData={primitiveData}
                  onPrimitiveClick={handlePrimitiveClick}
                  expanded={expandedPhases[phase]}
                  onToggle={() => togglePhase(phase)}
                />
              );
            })}
          </Stack>
        )}

        {/* Empty State */}
        {!primitiveData.loading && filteredPrimitives.length === 0 && searchQuery && (
          <Alert color="gray" variant="light">
            No primitives matching &ldquo;{searchQuery}&rdquo;
          </Alert>
        )}

        {/* Legend */}
        <Paper p="xs" withBorder bg="gray.0">
          <Group justify="center" gap="xl">
            <Group gap="xs">
              <Box w={12} h={12} style={{ borderLeft: '3px solid var(--mantine-color-red-6)' }} />
              <Text size="xs" c="dimmed">Life-Critical Primitive</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="xs" color="green" variant="light" radius="xl">
                <IconCheck size={8} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Healthy</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="xs" color="yellow" variant="light" radius="xl">
                <IconClock size={8} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Degraded</Text>
            </Group>
            <Group gap="xs">
              <ThemeIcon size="xs" color="red" variant="light" radius="xl">
                <IconX size={8} />
              </ThemeIcon>
              <Text size="xs" c="dimmed">Critical</Text>
            </Group>
          </Group>
        </Paper>
      </Stack>

      {/* Relationship Graph */}
      <Collapse in={showRelationships}>
        <Box mt="md">
          <PrimitiveRelationshipGraph
            selectedPrimitive={selectedPrimitive?.id}
            onPrimitiveClick={(type) => {
              const primitive = PRIMITIVES.find(p => p.id === type);
              if (primitive) handlePrimitiveClick(primitive);
            }}
          />
        </Box>
      </Collapse>

      {/* Detail Panel */}
      <PrimitiveDetailPanel
        primitiveType={selectedPrimitive?.id ?? null}
        primitiveData={selectedPrimitiveData}
        opened={panelOpened}
        onClose={closePanel}
        onView={(item) => {
          // For now, just open in edit mode for viewing
          handleEdit(item);
        }}
        onEdit={handleEdit}
        onCreate={handleCreate}
        onArchive={handleArchive}
      />

      {/* Form Modal */}
      {selectedPrimitive && (
        <PrimitiveFormModal
          opened={formModalOpened}
          onClose={closeFormModal}
          primitiveType={selectedPrimitive.id}
          mode={formMode}
          initialData={editingItem}
          onSuccess={handleFormSuccess}
        />
      )}
    </Container>
  );
}

// Export for use in routes
export default UnifiedPrimitiveForm;
